import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "../ui";
import { canInstall, chromeIntent, install, isStandalone, onInstallChange, phoneBrowser } from "./install";

/**
 * `/install` (I2): where the QR code in Settings → Capture from the phone leads. Chrome on Android gets an
 * "Install Drang" button; another Android browser gets "Open in Chrome"; an iPhone gets the steps for the
 * home screen. Nothing else on the page.
 */
export function InstallPage() {
  const { t } = useTranslation();
  const [ready, setReady] = useState(canInstall());
  const [done, setDone] = useState(isStandalone());
  const [waited, setWaited] = useState(false);
  const browser = phoneBrowser();

  useEffect(() => onInstallChange(() => setReady(canInstall())), []);
  // Chrome needs a moment to decide; if it never offers, its own menu still can (or the certificate is the problem)
  useEffect(() => {
    const id = setTimeout(() => setWaited(true), 4000);
    return () => clearTimeout(id);
  }, []);

  return (
    <main
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col gap-4 px-5 py-8"
      data-testid="install-page"
    >
      <h1 className="text-[20px] font-semibold">{t("install.title")}</h1>
      {done ? (
        <p className="text-[14px]" data-testid="install-done">
          {t("install.done")}
        </p>
      ) : browser === "android-other" ? (
        <>
          <p className="text-[14px]">{t("install.otherBrowser")}</p>
          <a
            href={chromeIntent(location.host, "/install")}
            className="inline-flex h-9 items-center justify-center rounded-sm border border-transparent bg-neutral-900 px-3.5 text-[13.5px] font-medium text-white dark:bg-neutral-100 dark:text-neutral-900"
            data-testid="install-open-chrome"
          >
            {t("install.openChrome")}
          </a>
        </>
      ) : browser === "ios" ? (
        <ol className="list-decimal pl-5 text-[14px] leading-relaxed" data-testid="install-ios">
          <li>{t("install.ios1")}</li>
          <li>{t("install.ios2")}</li>
          <li>{t("install.ios3")}</li>
        </ol>
      ) : (
        <>
          <p className="text-[14px]">{t("install.hint")}</p>
          <div>
            <Button
              disabled={!ready}
              onClick={() => void install().then((ok) => ok && setDone(true))}
              data-testid="install-button"
            >
              {t("install.install")}
            </Button>
          </div>
          {!ready && waited && (
            <p className="text-[13px] text-neutral-500" data-testid="install-manual">
              {t("install.manual")}
            </p>
          )}
        </>
      )}
      <p className="mt-auto text-[12.5px] text-neutral-500">
        <Link to="/c" className="text-accent hover:underline">
          {t("install.toCapture")}
        </Link>{" "}
        ·{" "}
        <Link to="/" className="text-accent hover:underline">
          {t("capture.openApp")}
        </Link>
      </p>
    </main>
  );
}
